import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Building2, Flame, FileSignature, DollarSign,
  TrendingUp, Users,
} from "lucide-react";
import ClockInWidget from "@/components/ClockInWidget";

function Kpi({ icon: Icon, label, value, testid, tone, onClick }) {
  const clickable = typeof onClick === "function";
  const tones = {
    accent:      "bg-[var(--app-surface-2)] text-[var(--app-accent-dark)]",
    hot:         "bg-red-50 text-red-600",
    success:     "bg-emerald-50 text-emerald-700",
    warning:     "bg-amber-50 text-amber-700",
    neutral:     "bg-[var(--app-surface)] text-[var(--app-text-muted)]",
  };
  const iconWrap = tones[tone] || tones.neutral;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`card-flat p-5 text-left w-full reveal ${
        clickable
          ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--app-accent)]/40"
          : "cursor-default"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
          {label}
        </div>
        <div className={`w-9 h-9 grid place-items-center rounded-full ${iconWrap}`}>
          <Icon size={15} strokeWidth={2.2} />
        </div>
      </div>
      <div className="kpi-num text-4xl mt-4">{value}</div>
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    properties: 0,
    hot_leads: 0,
    active_leads: 0,
    offers_sent: 0,
    contracts: 0,
    pipeline_value: 0,
  });
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 reveal">
        <div>
          <span className="app-eyebrow">/ Overview</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Dashboard</h1>
          <p className="text-[var(--app-text-muted)] mt-2 max-w-lg">
            All your acquisitions activity at a glance. Click any stat to jump in.
          </p>
        </div>
        {/* "Analyze new address" CTA moved to its own left-nav tab
            2026-02-17 by owner — see nav entry `/lookup` in
            SidebarContent.jsx. Dashboard header now stays purely
            informational. */}
      </div>

      <div className="mb-6">
        <ClockInWidget />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Kpi
          icon={Building2}
          label="Properties"
          value={stats.properties}
          testid="kpi-properties"
          tone="neutral"
          onClick={() => navigate("/properties")}
        />
        <Kpi
          icon={Flame}
          label="Hot Leads"
          value={stats.hot_leads}
          testid="kpi-hot-leads"
          tone="hot"
          onClick={() => navigate("/properties?filter=hot")}
        />
        <Kpi
          icon={Users}
          label="Active Leads"
          value={stats.active_leads}
          testid="kpi-active-leads"
          tone="neutral"
          onClick={() => navigate("/pipeline")}
        />
        <Kpi
          icon={FileSignature}
          label="Offers Sent"
          value={stats.offers_sent}
          testid="kpi-offers-sent"
          tone="accent"
          onClick={() => navigate("/offers")}
        />
        <Kpi
          icon={TrendingUp}
          label="Contracts"
          value={stats.contracts}
          testid="kpi-contracts"
          tone="success"
          onClick={() => navigate("/pipeline?stage=contract")}
        />
        <Kpi
          icon={DollarSign}
          label="Pipeline Value"
          value={money(stats.pipeline_value)}
          testid="kpi-pipeline-value"
          tone="accent"
          onClick={() => navigate("/pipeline")}
        />
      </div>
    </div>
  );
}
